import React, { useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { Box, Grid, makeStyles, Typography } from 'transfix-ui/core'
import { IConnectionTypes, IFtpConnectionTypes } from 'types/graphqlTypes'
import { Button } from 'transfix-ui'
import { useModalState } from 'utils/hooks'
import { CONNECTION_CONFIG_TYPES } from '../ConnectionConfigTypes'
import { InboundConnectionConfigDetails } from './InboundConnectionConfigDetails'
import { InboundConnectionConfigsTable } from './InboundConnectionConfigsTable'
import { useEdiInboundConnectionConfig } from './hooks/useEdiInboundConnectionConfig'
import { AddInboundConnectionConfigModal } from './modals/AddInboundConnectionConfigModal'
import { TAddInboundConnectionConfigModalSchema } from './schema/AddInboundConnectionConfigModalSchema'

const useInboundConnectionConfigsStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
    maxWidth: '100vw',
    display: 'flex',
    overflowX: 'clip',
  },
  button: {
    marginTop: theme.spacing(1),
  },
  text: {
    marginLeft: theme.spacing(1),
  },
}))

export const InboundConnectionConfigs = () => {
  const { configId: id } = useParams<{ configId: string }>()
  const [isSidebarOpen, setIsSidebarOpen] = useState(Boolean(id))
  const history = useHistory()
  const classes = useInboundConnectionConfigsStyles()

  const { createToggleHandler, modalState } = useModalState({
    dispatchAddModal: false,
  })
  const openAddHandler = createToggleHandler('dispatchAddModal', true)
  const closeAddHandler = createToggleHandler('dispatchAddModal', false)

  const {
    createAs2InboundConnectionConfig,
    createFtpInboundConnectionConfig,
    updateAs2InboundConnectionConfig,
    updateFtpInboundConnectionConfig,
  } = useEdiInboundConnectionConfig()

  const submitHandler = (data: TAddInboundConnectionConfigModalSchema) => {
    const { connectionType } = data
    const isEdit = !!data.id

    if (connectionType === IConnectionTypes.AS2) {
      if (isEdit) {
        updateAs2InboundConnectionConfig({
          id: data?.id,
          as2ConnectionConfigId: data?.as2ConnectionConfigId,
          disabled: data?.disabled,
          environment: data?.environment,
          filenameGlob: data?.filenameGlob,
          partnerApplicationId: data?.company.id,
          partnerApplicationType: data?.company.data.__typename,
          send997s: data?.send997s,
        })
      } else {
        createAs2InboundConnectionConfig({
          as2ConnectionConfigId: data?.as2ConnectionConfigId,
          disabled: data?.disabled,
          environment: data?.environment,
          filenameGlob: data?.filenameGlob,
          partnerApplicationId: data?.company.id,
          partnerApplicationType: data?.company.data.__typename,
          send997s: data?.send997s,
        })
      }
    } else {
      let ftpConnectionType: IFtpConnectionTypes

      if (connectionType === 'SFTP_WITH_PASSWORD') {
        ftpConnectionType = IFtpConnectionTypes.SFTP_WITH_PASSWORD
      } else if (connectionType === 'SFTP_WITH_PRIVATE_KEY') {
        ftpConnectionType = IFtpConnectionTypes.SFTP_WITH_PRIVATE_KEY
      } else {
        ftpConnectionType = IFtpConnectionTypes.FTP
      }

      if (isEdit) {
        updateFtpInboundConnectionConfig({
          id: data?.id,
          connectionType: ftpConnectionType,
          disabled: data?.disabled,
          environment: data?.environment,
          filenameGlob: data?.filenameGlob,
          partnerApplicationId: data?.company.id,
          partnerApplicationType: data?.company.data.__typename,
          username: data?.username,
          host: data?.host,
          port: data?.port,
          pathPrefix: data?.pathPrefix,
          secretKey: data?.secretKey,
          send997s: data?.send997s,
        })
      } else {
        createFtpInboundConnectionConfig({
          connectionType: ftpConnectionType,
          disabled: data?.disabled,
          environment: data?.environment,
          filenameGlob: data?.filenameGlob,
          partnerApplicationId: data?.company.id,
          partnerApplicationType: data?.company.data.__typename,
          username: data?.username,
          host: data?.host,
          port: data?.port,
          pathPrefix: data?.pathPrefix,
          secretKey: data?.secretKey,
          send997s: data?.send997s,
        })
      }
    }
  }

  /*
   * When URL param id updates, it sets new state of Sidebar panel
   */
  useEffect(() => {
    setIsSidebarOpen(Boolean(id))
  }, [id])

  const closeDialogHandler = () => {
    setIsSidebarOpen(false)
    history.replace(`/connection-configs/${CONNECTION_CONFIG_TYPES.inbound}`)
  }

  return (
    <div className={classes.root}>
      <Box pl={3}>
        <AddInboundConnectionConfigModal
          title='Add a new Inbound Connection Config'
          onClose={closeAddHandler}
          open={modalState.dispatchAddModal}
          onSubmit={submitHandler}
        />
        <Grid container direction='row' justifyContent='space-between' alignItems='center'>
          <Grid item xs={12} md='auto'>
            <Typography
              variant='h3'
              className={classes.text}
              color='primary'
              display='inline'
              component='div'>
              EDI Inbound Connection Configurations
            </Typography>
          </Grid>
          <Grid item xs={12} md='auto'>
            <Button
              color='primary'
              size='small'
              data-test='add-inbound-config-button'
              className={classes.button}
              onClick={openAddHandler}>
              Add New
            </Button>
          </Grid>
        </Grid>
        <Grid container direction='row' alignItems='center'>
          <Grid item>
            <InboundConnectionConfigsTable onEdit={submitHandler} />
          </Grid>
        </Grid>
        {isSidebarOpen && (
          <InboundConnectionConfigDetails
            onEdit={submitHandler}
            onSidebarClose={closeDialogHandler}
          />
        )}
      </Box>
    </div>
  )
}
